import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from '../components/ui/Table';
import { SearchFilter } from '../components/ui/SearchFilter';
import { Card } from '../components/ui/Card';
import { Plus, Edit2, Tag, Copy, ToggleLeft, ToggleRight, RotateCcw } from 'lucide-react';
import {
  listPromoCodes,
  createPromoCode,
  bulkGeneratePromoCodes,
  activatePromoCode,
  deactivatePromoCode,
  listRestaurants
} from '../services/resortAdminApi';
import { validatePromoCodeForm, getValidationErrorMessage } from '../utils/promoCodeValidation';

interface PromoCode {
  name: string;
  code: string;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  min_order_amount: number;
  max_order_amount: number;
  restaurant?: string;
  restaurant_name?: string;
  max_uses_per_user: number;
  start_date: string;
  end_date: string;
  status: 'Active' | 'Inactive';
  is_redeemed: number;
  redeemed_by_order?: string;
  batch_id?: string;
}

interface RestaurantOption {
  name: string;
  restaurant_name: string;
}

export const PromoCodes: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [bulkModalError, setBulkModalError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [redeemedFilter, setRedeemedFilter] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');

  // Restaurant options for dropdowns
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([]);

  // Single code form
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'fixed' as 'fixed' | 'percentage',
    discount_value: '' as number | '',
    min_order_amount: 0,
    max_order_amount: 0,
    restaurant: '' as string,
    max_uses_per_user: 0,
    start_date: '',
    end_date: '',
    status: 'Active' as 'Active' | 'Inactive'
  });

  // Bulk generate form
  const [bulkFormData, setBulkFormData] = useState({
    count: 10,
    discount_type: 'fixed' as 'fixed' | 'percentage',
    discount_value: '' as number | '',
    min_order_amount: 0,
    max_order_amount: 0,
    restaurant: '' as string,
    max_uses_per_user: 0,
    start_date: '',
    end_date: '',
    status: 'Active' as 'Active' | 'Inactive',
    code_prefix: ''
  });

  // Load promo codes
  const loadPromoCodes = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.discount_type = typeFilter;
      if (redeemedFilter !== '') filters.is_redeemed = redeemedFilter === 'true';
      if (startDateFilter) filters.start_date = startDateFilter;
      if (endDateFilter) filters.end_date = endDateFilter;

      const result = await listPromoCodes(
        Object.keys(filters).length > 0 ? filters : undefined,
        searchTerm || undefined,
        page,
        pageSize
      );
      
      if (result && result.data) {
        setPromoCodes(result.data);
        setTotal(result.pagination?.total || 0);
      }
    } catch (error: any) {
      console.error('Error loading promo codes:', error);
      setError(error.message || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPromoCodes();
  }, [page, pageSize, searchTerm, statusFilter, typeFilter, redeemedFilter, startDateFilter, endDateFilter]);

  // Load restaurant options on mount
  useEffect(() => {
    const loadRestaurants = async () => {
      try {
        const result = await listRestaurants(undefined, 1, 200);
        const data = result?.data || result || [];
        setRestaurants(data.map((r: any) => ({ name: r.name, restaurant_name: r.restaurant_name || r.name })));
      } catch (err) {
        console.error('Failed to load restaurants:', err);
      }
    };
    loadRestaurants();
  }, []);

  // Table columns
  const columns = [
    {
      key: 'code',
      header: 'Code',
      render: (value: string) => (
        <span className="font-mono font-medium text-indigo-600">{value}</span>
      )
    },
    {
      key: 'discount_type',
      header: 'Type',
      render: (value: string, row: PromoCode) => (
        <span>
          {value === 'fixed' ? `€${row.discount_value}` : `${row.discount_value}%`}
        </span>
      )
    },
    {
      key: 'min_order_amount',
      header: 'Min. Order',
      render: (value: number) => value > 0 ? `€${value}` : '-'
    },
    {
      key: 'restaurant_name',
      header: 'Restaurant',
      render: (value: string | null, row: PromoCode) => value || 'All'
    },
    {
      key: 'start_date',
      header: 'Valid From'
    },
    {
      key: 'end_date',
      header: 'Valid Until'
    },
    {
      key: 'status',
      header: 'Status',
      render: (value: string) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value}
        </span>
      )
    },
    {
      key: 'is_redeemed',
      header: 'Redeemed',
      render: (value: number, row: PromoCode) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_: any, row: PromoCode) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/promo-codes/${row.name}`)}
            className="text-indigo-600 hover:text-indigo-900"
            title="Edit"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleToggleStatus(row)}
            className={row.status === 'Active' ? 'text-green-600 hover:text-green-900' : 'text-orange-600 hover:text-orange-900'}
            title={row.status === 'Active' ? 'Deactivate' : 'Activate'}
            disabled={row.is_redeemed === 1}
          >
            {row.status === 'Active' ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
          </button>
          <button
            onClick={() => copyToClipboard(row.code)}
            className="text-gray-600 hover:text-gray-900"
            title="Copy code"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess(`Code "${text}" copied to clipboard`);
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleToggleStatus = async (promo: PromoCode) => {
    try {
      if (promo.status === 'Active') {
        await deactivatePromoCode(promo.name);
      } else {
        await activatePromoCode(promo.name);
      }
      loadPromoCodes();
      setSuccess(`Promo code ${promo.status === 'Active' ? 'deactivated' : 'activated'} successfully`);
    } catch (error: any) {
      setError(error.message || 'Failed to update status');
    }
  };

  const handleResetFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
    setRedeemedFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setSearchTerm('');
    setPage(1); // Reset to first page
  };

  const handleCreateSingle = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError('');
    
    // Frontend validation
    const validationErrors = validatePromoCodeForm(formData, false);
    if (validationErrors.length > 0) {
      setModalError(getValidationErrorMessage(validationErrors));
      return;
    }

    // At this point, validation passed, so discount_value must be a valid number
    if (formData.discount_value === '' || formData.discount_value === undefined) {
      setModalError('The discount value is required and cannot be 0');
      return;
    }

    try {
      const result = await createPromoCode({
        ...formData,
        discount_value: formData.discount_value as number,
        restaurant: formData.restaurant || null,
      });

      // Check if backend returned an error response
      if (result && result.ok === false) {
        const errorCode = result.error_code;
        const errorMessage = result.error_message || 'Failed to create promo code';

        if (errorCode === 'PROMO_CODE_ALREADY_SET') {
          setModalError(`This promo code "${formData.code.toUpperCase()}" already exists. Please use a different code.`);
        } else {
          setModalError(errorMessage);
        }
        return;
      }

      setSuccess('Promo code created successfully');
      setModalError('');
      setShowCreateModal(false);
      setFormData({
        code: '',
        discount_type: 'fixed',
        discount_value: '',
        min_order_amount: 0,
        max_order_amount: 0,
        restaurant: '',
        max_uses_per_user: 0,
        start_date: '',
        end_date: '',
        status: 'Active'
      });
      loadPromoCodes();
    } catch (error: any) {
      // Extract user-friendly error message
      let errorMessage = 'Failed to create promo code';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?._error_message) {
        errorMessage = error.response.data._error_message;
      } else if (error.response?.data?.exception) {
        errorMessage = error.response.data.exception;
      }
      setModalError(errorMessage);
    }
  };

  const handleBulkGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBulkModalError('');

    // Frontend validation
    const validationErrors = validatePromoCodeForm(bulkFormData, false);
    if (validationErrors.length > 0) {
      setBulkModalError(getValidationErrorMessage(validationErrors));
      return;
    }

    // At this point, validation passed, so discount_value must be a valid number
    if (bulkFormData.discount_value === '' || bulkFormData.discount_value === undefined) {
      setBulkModalError('The discount value is required and cannot be 0');
      return;
    }

    try {
      const result = await bulkGeneratePromoCodes({
        ...bulkFormData,
        discount_value: bulkFormData.discount_value as number,
        restaurant: bulkFormData.restaurant || null,
      });
      setSuccess(`Successfully generated ${result.count} ${result.count === 1 ? 'promo code' : 'promo codes'}`);
      setBulkModalError('');
      setShowBulkModal(false);
      setBulkFormData({
        count: 10,
        discount_type: 'fixed',
        discount_value: '',
        min_order_amount: 0,
        max_order_amount: 0,
        restaurant: '',
        max_uses_per_user: 0,
        start_date: '',
        end_date: '',
        status: 'Active',
        code_prefix: ''
      });
      loadPromoCodes();
    } catch (error: any) {
      // Extract user-friendly error message
      let errorMessage = 'Failed to generate promo codes';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?._error_message) {
        errorMessage = error.response.data._error_message;
      } else if (error.response?.data?.exception) {
        errorMessage = error.response.data.exception;
      }
      setBulkModalError(errorMessage);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Promo Codes</h1>
          <p className="mt-1 text-sm text-gray-600">Manage discount codes for food and grocery orders</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setBulkModalError('');
              setShowBulkModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-alps-primary shadow-sm text-sm font-medium rounded-md text-alps-primary bg-white hover:bg-gray-50"
          >
            <Tag className="h-4 w-4 mr-2" />
            Bulk Generate
          </button>
          <button
            onClick={() => {
              setModalError('');
              setShowCreateModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Code
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All Types</option>
            <option value="fixed">Fixed Amount</option>
            <option value="percentage">Percentage</option>
          </select>
          <select
            value={redeemedFilter}
            onChange={(e) => setRedeemedFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="false">Not Redeemed</option>
            <option value="true">Redeemed</option>
          </select>
          <input
            type="date"
            value={startDateFilter}
            onChange={(e) => setStartDateFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="Valid from"
          />
          <input
            type="date"
            value={endDateFilter}
            onChange={(e) => setEndDateFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="Valid until"
          />
          <button
            onClick={handleResetFilters}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </button>
        </div>
        <SearchFilter
          placeholder="Search by code..."
          onSearch={setSearchTerm}
          onFilter={() => {}}
          initialValue={searchTerm}
        />
        <Table
          columns={columns}
          data={promoCodes}
          isLoading={loading}
          emptyMessage="No promo codes found"
          pagination={{
            page,
            page_size: pageSize,
            total
          }}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      {/* Create Single Code Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Create Promo Code</h3>
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  setModalError('');
                }} 
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            {modalError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{modalError}</p>
              </div>
            )}
            <form onSubmit={handleCreateSingle} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Code <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., SUMMER20"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Discount Type <span className="text-red-500">*</span></label>
                  <select
                    required
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as 'fixed' | 'percentage' })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  >
                    <option value="fixed">Fixed Amount (€)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Value <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.discount_value === '' ? '' : formData.discount_value}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ 
                        ...formData, 
                        discount_value: value === '' ? '' : parseFloat(value) || ''
                      });
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Min. Order Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.min_order_amount}
                  onChange={(e) => setFormData({ ...formData, min_order_amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Order Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.max_order_amount}
                  onChange={(e) => setFormData({ ...formData, max_order_amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">0 = no limit</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Restaurant</label>
                <select
                  value={formData.restaurant}
                  onChange={(e) => setFormData({ ...formData, restaurant: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                >
                  <option value="">All restaurants</option>
                  {restaurants.map(r => (
                    <option key={r.name} value={r.name}>{r.restaurant_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Uses Per User</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.max_uses_per_user}
                  onChange={(e) => setFormData({ ...formData, max_uses_per_user: parseInt(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">0 = single-use (one use total), 1+ = multi-use per user</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    required
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setModalError('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-alps-primary text-white rounded-md hover:bg-alps-primary-dark"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Generate Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Bulk Generate Promo Codes</h3>
              <button 
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkModalError('');
                }} 
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            {bulkModalError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{bulkModalError}</p>
              </div>
            )}
            <form onSubmit={handleBulkGenerate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Number of Codes <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    required
                    min="1"
                    max="1000"
                    value={bulkFormData.count}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, count: parseInt(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Code Prefix</label>
                  <input
                    type="text"
                    maxLength={4}
                    value={bulkFormData.code_prefix}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, code_prefix: e.target.value.toUpperCase() })}
                    placeholder="e.g., SUM"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Discount Type <span className="text-red-500">*</span></label>
                  <select
                    required
                    value={bulkFormData.discount_type}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, discount_type: e.target.value as 'fixed' | 'percentage' })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  >
                    <option value="fixed">Fixed Amount (€)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Value <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={bulkFormData.discount_value === '' ? '' : bulkFormData.discount_value}
                    onChange={(e) => {
                      const value = e.target.value;
                      setBulkFormData({ 
                        ...bulkFormData, 
                        discount_value: value === '' ? '' : parseFloat(value) || ''
                      });
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Min. Order Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={bulkFormData.min_order_amount}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, min_order_amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Order Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={bulkFormData.max_order_amount}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, max_order_amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">0 = no limit</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Restaurant</label>
                <select
                  value={bulkFormData.restaurant}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, restaurant: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                >
                  <option value="">All restaurants</option>
                  {restaurants.map(r => (
                    <option key={r.name} value={r.name}>{r.restaurant_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Uses Per User</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={bulkFormData.max_uses_per_user}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, max_uses_per_user: parseInt(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">0 = single-use (one use total), 1+ = multi-use per user</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    required
                    value={bulkFormData.start_date}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, start_date: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    required
                    value={bulkFormData.end_date}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, end_date: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkModal(false);
                    setBulkModalError('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-alps-primary text-white rounded-md hover:bg-alps-primary-dark"
                >
                  Generate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

